import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface StaleOperationStats {
  enhancementOps: number;
  quizOps: number;
  practiceOps: number;
  total: number;
}

export interface CorruptedModuleStats {
  count: number;
  modules: Array<{ module_id: string; module_title: string; course_id: string }>;
}

export interface OrphanedRecordStats {
  content: number;
  quizzes: number;
  labs: number;
  total: number;
}

export const adminOperationsService = {
  /**
   * Get count of stale operations (stuck in_progress for >10 min)
   */
  async getStaleOperationStats(): Promise<StaleOperationStats> {
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();

    const [enhancement, quiz, practice] = await Promise.all([
      supabase
        .from('content_enhancement_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('last_heartbeat', tenMinutesAgo),
      supabase
        .from('quiz_generation_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('last_heartbeat', tenMinutesAgo),
      supabase
        .from('practice_generation_operations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('last_heartbeat', tenMinutesAgo),
    ]);

    return {
      enhancementOps: enhancement.count || 0,
      quizOps: quiz.count || 0,
      practiceOps: practice.count || 0,
      total: (enhancement.count || 0) + (quiz.count || 0) + (practice.count || 0),
    };
  },

  /**
   * Stop all stale operations via edge function (has proper permissions)
   */
  async stopAllStaleOperations(): Promise<{ stopped: number; errors: string[] }> {
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-stuck-operations');
      
      if (error) throw error;
      
      const results: Record<string, number> = data?.results || {};
      const totalCleaned = Object.values(results).reduce((a, b) => a + b, 0);
      console.log(`✅ Stopped ${totalCleaned} stale operations, released ${data?.locksReleased || 0} locks`);
      
      return { stopped: totalCleaned, errors: [] };
    } catch (e: any) {
      console.error('Stop stale operations error:', e);
      return { stopped: 0, errors: [e.message] };
    }
  },

  /**
   * Get corrupted modules (with bad titles like "please provide", "could you please", etc.)
   */
  async getCorruptedModules(): Promise<CorruptedModuleStats> {
    // Fetch all enabled modules and filter client-side for corrupted titles
    const { data: allModules } = await supabase
      .from('course_modules')
      .select('module_id, module_title, course_id')
      .eq('is_enabled', true);

    const corrupted = allModules?.filter(m => {
      const title = m.module_title?.toLowerCase() || '';
      const originalTitle = m.module_title || '';
      return (
        // Prompt leakage patterns
        title.includes('please provide') ||
        title.includes('could you please') ||
        title.includes('i need') ||
        title.includes('can you') ||
        title.includes('once you provide') ||
        // Code snippets
        title.includes('go install') ||
        title.includes('npm install') ||
        title.includes('pip install') ||
        title.startsWith('import ') ||
        title.includes('this command') ||
        title.includes('this is a') ||
        title.includes('this opens') ||
        title.includes('this should') ||
        title.includes('this requires') ||
        // Comment patterns
        title.includes('---') ||
        title.includes('same as above') ||
        title.includes('helper function') ||
        title.includes('configuration') && title.includes('---') ||
        // HTML/code
        originalTitle.includes('<') ||
        originalTitle.includes('>') ||
        originalTitle.includes('[[') ||
        originalTitle.includes(']]') ||
        // Length issues
        originalTitle.length > 80 ||
        originalTitle.length < 5 ||
        // Incomplete sentences
        title.includes('for example') ||
        title.includes('interpretation:') ||
        title.includes('we create') ||
        title.includes('ensure your')
      );
    }) || [];

    return {
      count: corrupted.length,
      modules: corrupted
    };
  },

  /**
   * Disable corrupted modules
   */
  async disableCorruptedModules(): Promise<{ disabled: number; errors: string[] }> {
    const { modules } = await this.getCorruptedModules();
    const moduleIds = modules.map(m => m.module_id);
    
    if (moduleIds.length === 0) {
      return { disabled: 0, errors: [] };
    }

    const { error } = await supabase
      .from('course_modules')
      .update({ is_enabled: false })
      .in('module_id', moduleIds);

    if (error) {
      return { disabled: 0, errors: [error.message] };
    }

    // Log to content_quality_alerts
    for (const module of modules) {
      await supabase.from('content_quality_alerts').insert({
        alert_type: 'corrupted_module_title',
        module_id: module.module_id,
        course_id: module.course_id,
        issue_description: `Module disabled due to corrupted title: "${module.module_title?.substring(0, 100)}"`,
        severity: 'high',
        metadata: { original_title: module.module_title, disabled_at: new Date().toISOString() }
      });
    }

    console.log(`✅ Disabled ${moduleIds.length} corrupted modules`);
    return { disabled: moduleIds.length, errors: [] };
  },

  /**
   * Get orphaned record stats (content/quizzes referencing non-existent modules)
   */
  async getOrphanedRecordStats(): Promise<OrphanedRecordStats> {
    // Get ALL module_ids from course_modules (not just enabled)
    const { data: allModules } = await supabase
      .from('course_modules')
      .select('module_id');
    
    const validModuleIds = new Set(allModules?.map(m => m.module_id) || []);

    // Check which records reference module_ids that DON'T EXIST AT ALL
    const { data: allContent } = await supabase.from('module_content').select('module_id');
    const { data: allQuizzes } = await supabase.from('module_quizzes').select('module_id');
    const { data: allLabs } = await supabase.from('generated_practice_labs').select('module_id');

    const orphanedContent = allContent?.filter(c => !validModuleIds.has(c.module_id)).length || 0;
    const orphanedQuizzes = allQuizzes?.filter(q => !validModuleIds.has(q.module_id)).length || 0;
    const orphanedLabs = allLabs?.filter(l => !validModuleIds.has(l.module_id)).length || 0;

    return {
      content: orphanedContent,
      quizzes: orphanedQuizzes,
      labs: orphanedLabs,
      total: orphanedContent + orphanedQuizzes + orphanedLabs
    };
  },

  /**
   * Clean all TRULY orphaned records (module_id doesn't exist in course_modules at all)
   */
  async cleanAllOrphanedRecords(): Promise<{ 
    archivedContent: number; 
    deletedQuizzes: number; 
    deletedLabs: number; 
    errors: string[] 
  }> {
    const errors: string[] = [];
    let archivedContent = 0;
    let deletedQuizzes = 0;
    let deletedLabs = 0;

    // Get ALL module IDs that exist in course_modules (not just enabled)
    const { data: allModules } = await supabase
      .from('course_modules')
      .select('module_id');
    
    const validModuleIds = new Set(allModules?.map(m => m.module_id) || []);

    // 1. Archive and delete orphaned content
    const { data: allContent } = await supabase
      .from('module_content')
      .select('id, module_id, content, course_id');
    
    const orphanedContent = allContent?.filter(c => !validModuleIds.has(c.module_id)) || [];
    
    for (const record of orphanedContent) {
      try {
        await supabase.from('ai_archived_content').insert({
          source_type: 'module_content',
          source_id: record.id,
          content: record.content?.substring(0, 10000),
          title: `Truly orphaned content: ${record.module_id}`,
          tags: ['orphaned', 'cleanup', 'nonexistent_module'],
          metadata: { course_id: record.course_id, module_id: record.module_id, archived_at: new Date().toISOString() }
        });
      } catch (e: any) {
        errors.push(`Archive content ${record.id}: ${e.message}`);
      }
    }

    if (orphanedContent.length > 0) {
      const { error } = await supabase
        .from('module_content')
        .delete()
        .in('id', orphanedContent.map(r => r.id));
      
      if (error) errors.push(`Delete content: ${error.message}`);
      else archivedContent = orphanedContent.length;
    }

    // 2. Delete orphaned quizzes
    const { data: allQuizzes } = await supabase
      .from('module_quizzes')
      .select('id, module_id');
    
    const orphanedQuizzes = allQuizzes?.filter(q => !validModuleIds.has(q.module_id)) || [];
    
    if (orphanedQuizzes.length > 0) {
      const { error } = await supabase
        .from('module_quizzes')
        .delete()
        .in('id', orphanedQuizzes.map(r => r.id));
      
      if (error) errors.push(`Delete quizzes: ${error.message}`);
      else deletedQuizzes = orphanedQuizzes.length;
    }

    // 3. Delete orphaned labs (the 410 that don't exist in course_modules)
    const { data: allLabs } = await supabase
      .from('generated_practice_labs')
      .select('id, module_id');
    
    const orphanedLabs = allLabs?.filter(l => !validModuleIds.has(l.module_id)) || [];
    
    if (orphanedLabs.length > 0) {
      const { error } = await supabase
        .from('generated_practice_labs')
        .delete()
        .in('id', orphanedLabs.map(r => r.id));
      
      if (error) errors.push(`Delete labs: ${error.message}`);
      else deletedLabs = orphanedLabs.length;
    }

    console.log(`✅ Cleaned truly orphaned records: ${archivedContent} content, ${deletedQuizzes} quizzes, ${deletedLabs} labs`);
    return { archivedContent, deletedQuizzes, deletedLabs, errors };
  },

  /**
   * Trigger universal content generation with proper tracking
   */
  async triggerUniversalGeneration(type: 'content' | 'quizzes' | 'labs' | 'all'): Promise<{ 
    success: boolean; 
    operationId?: string; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('universal-content-generator', {
        body: { type, maxItems: 50 }
      });

      if (error) throw error;
      
      return { success: true, operationId: data?.operationId };
    } catch (e: any) {
      console.error('Universal generation error:', e);
      return { success: false, error: e.message };
    }
  },

  /**
   * Get current content gaps (modules missing content, quizzes, or labs)
   */
  async getContentGaps(): Promise<{ content: number; quizzes: number; labs: number }> {
    // Get all enabled modules
    const { data: modules } = await supabase
      .from('course_modules')
      .select('module_id')
      .eq('is_enabled', true);
    
    const moduleIds = new Set(modules?.map(m => m.module_id) || []);
    const totalModules = moduleIds.size;

    // Get modules with content
    const { data: contentModules } = await supabase
      .from('module_content')
      .select('module_id');
    const withContent = new Set(contentModules?.map(c => c.module_id) || []);

    // Get modules with quizzes
    const { data: quizModules } = await supabase
      .from('module_quizzes')
      .select('module_id');
    const withQuizzes = new Set(quizModules?.map(q => q.module_id) || []);

    // Get modules with labs
    const { data: labModules } = await supabase
      .from('generated_practice_labs')
      .select('module_id');
    const withLabs = new Set(labModules?.map(l => l.module_id) || []);

    // Calculate gaps for enabled modules only
    let contentGap = 0;
    let quizGap = 0;
    let labGap = 0;

    moduleIds.forEach(id => {
      if (!withContent.has(id)) contentGap++;
      if (!withQuizzes.has(id)) quizGap++;
      if (!withLabs.has(id)) labGap++;
    });

    return { content: contentGap, quizzes: quizGap, labs: labGap };
  },

  /**
   * Get operation status by ID
   */
  async getOperationStatus(operationId: string): Promise<{
    status: string;
    processed_items: number;
    total_items: number;
  } | null> {
    const { data } = await supabase
      .from('content_enhancement_operations')
      .select('status, processed_items, total_items')
      .eq('id', operationId)
      .single();
    
    return data;
  },

  /**
   * Get the currently active operation (if any)
   */
  async getActiveOperation(): Promise<{
    id: string;
    status: string;
    processed_items: number;
    total_items: number;
  } | null> {
    const { data } = await supabase
      .from('content_enhancement_operations')
      .select('id, status, processed_items, total_items')
      .eq('status', 'in_progress')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    return data;
  }
};
